import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 elevation-1 hover:elevation-2",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 elevation-1 hover:elevation-2 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border-2 border-foreground/20 bg-[var(--surface-container-high)] hover:bg-[var(--surface-container)] hover:border-foreground/30 text-foreground",
        secondary: "bg-[var(--secondary-container)] text-[var(--secondary-container-foreground)] hover:opacity-80 elevation-1 hover:elevation-2",
        ghost: "hover:bg-[var(--surface-variant)] hover:text-foreground active:bg-[var(--surface-container)]",
        link: "text-primary underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        default: "h-11 px-6 py-2.5 has-[>svg]:px-4 text-base",
        sm: "h-9 rounded-full gap-1.5 px-4 has-[>svg]:px-3 text-sm",
        lg: "h-12 rounded-full px-8 has-[>svg]:px-6 text-base",
        icon: "size-11 rounded-full",
        "icon-sm": "size-9 rounded-full",
        "icon-lg": "size-12 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
